// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@sc/App/Components/Notify/Notify";
import CartLineQuantity, { CartLineQuantityStyles } from "@sc/Overrides/Widgets/Cart/CartLineQuantity";
import Remove from "@sc/XDL/Icons/Remove";
import React from "react";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

export interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    imageLink?: LinkProps;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    imageAndDescriptionWrapper?: InjectableCss;
    lineDescription?: LinkPresentationProps;
    qtyWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    extendedPriceWrapper?: InjectableCss;
    basicProductPriceStyles?: ProductPriceStyles;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    notify?: TypographyProps;
    removeWrapper?: InjectableCss;
    qtyPriceWrapper?: InjectableCss;
    erpNumberTextSmallHeading?: SmallHeadingAndTextStyles;
    partNumbersWrapper?: InjectableCss;
}

export const miniCartLineStyles: MiniCartLineStyles = {};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLine = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}: Props) => {
    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.detailsWrapper}>
                    <StyledWrapper {...styles.imageWrapper}>
                        <Link
                            {...styles.imageLink}
                            href={cartLine.productUri}
                            onClick={hideTippy}
                            aria-label={`${cartLine.altText} link`}
                        >
                            <LazyImage
                                width="91"
                                height="91"
                                src={cartLine.smallImagePath}
                                altText={cartLine.altText}
                                {...styles.cartLineImage}
                            />
                        </Link>
                    </StyledWrapper>
                    <StyledWrapper {...styles.contentWrapper}>
                        <Link
                            href={cartLine.productUri}
                            onClick={hideTippy}
                            {...styles.lineDescription}
                            aria-label={`${cartLine.shortDescription} Link`}
                        >
                            {cartLine.brand?.name && `${cartLine.brand?.name} : `}
                            {cartLine.shortDescription}
                        </Link>
                        <StyledWrapper {...styles.partNumbersWrapper}>
                            {cartLine.erpNumber && (
                                <SmallHeadingAndText
                                    heading={translate("Item #")}
                                    text={cartLine.erpNumber}
                                    extendedStyles={styles.erpNumberTextSmallHeading}
                                />
                            )}
                            {cartLine.manufacturerItem && (
                                <SmallHeadingAndText
                                    heading={translate("Mfg #")}
                                    text={cartLine.manufacturerItem}
                                    extendedStyles={styles.erpNumberTextSmallHeading}
                                />
                            )}
                            {cartLine.customerName && (
                                <SmallHeadingAndText
                                    heading={translate("CPN")}
                                    text={cartLine.customerName}
                                    extendedStyles={styles.erpNumberTextSmallHeading}
                                />
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.qtyPriceWrapper}>
                            <StyledWrapper {...styles.qtyWrapper}>
                                <CartLineQuantity
                                    cart={cart as Cart}
                                    editable={true}
                                    extendedStyles={styles.cartLineQuantityStyles}
                                    onQtyOrderedChange={(inputQty: number) => {
                                        qtyOrderedChangeHandler(cartLine, inputQty);
                                    }}
                                    displayTooltip={true}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.extendedPriceWrapper}>
                                {/* TODO : Re-test when pricing component is ready */}
                                <ProductPrice
                                    showLabel={false}
                                    product={cartLine}
                                    {...{ showFailedToLoadPricingIcon: true }}
                                    extendedStyles={styles.basicProductPriceStyles}
                                />
                            </StyledWrapper>
                        </StyledWrapper>
                        {errorMessages && (
                            <StyledWrapper {...styles.errorMessagesWrapper}>
                                {errorMessages.map(message => (
                                    <Notify
                                        {...styles.notify}
                                        type="danger"
                                        data-test-selector="cartline_errorMessage"
                                        key={`error_${cartLine.id}`}
                                    >
                                        {message}
                                    </Notify>
                                ))}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.removeWrapper}>
                        <Link
                            onClick={() => removeCartLineClickHandler(cartLine)}
                            {...styles.removeCartLineLink}
                            aria-label="Remove Cart Line"
                        >
                            <Icon src={Remove} size={16} />
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLine;
